// skills/pyre/pyre.js
// Pyre skill logic

import { LOGS, TOOLS, RITUALS, getAvailableRituals, canLogBeUsedInRitual, getRitualAshesOutput } from './pyreData.js';
import { gameState } from '../../game/gameState.js';
import { addXP } from '../../systems/experience.js';
import { SKILLS } from '../../game/constants.js';
import logger from '../../utils/logger.js';
import { toastWarning, toastSuccess } from '../../ui/toast.js';
import { awardDrops, canFitItem } from '../../systems/drops.js';
import { rollForCorruption } from '../../systems/corruption.js';
import { checkPyreLoreTriggers, showPyreVeilLoreQueue } from '../../ui/pyreVeilLoreModal.js';
import {
    rollTalentProc,
    getTalentFlatBonus,
    getTalentLootBonus,
    incrementStackCount,
    resetStackCount
} from '../../systems/talentTree/talentTree.js';
import { POTION_RECIPES } from '../alchemy/alchemyData.js';
import { recordTalentProc } from '../../ui/talentActivity.js';
import { BaseSkill } from '../../core/BaseSkill.js';

class PyreSkill extends BaseSkill {
    constructor() {
        super(SKILLS.PYRE, TOOLS, 'TINDERBOX');
        // Track instant burn proc (Inferno talent)
        this.nextBurnInstant = false;
    }

    /**
     * Get the currently equipped tool object (alias for BaseSkill method)
     */
    get currentTool() {
        return this.getCurrentTool();
    }

    canBurn(logsId) {
        // Find logs by id property, not by object key
        const logs = Object.values(LOGS).find(l => l.id === logsId);
        if (!logs) return { success: false, reason: 'Logs not found' };

        const skill = gameState.getSkill(SKILLS.PYRE);
        if (skill.level < logs.level) {
            return { success: false, reason: `Requires level ${logs.level} Pyre` };
        }

        // Check for required logs in inventory
        const hasLogs = gameState.getItemCount(logs.inputItem) >= logs.inputQuantity;
        if (!hasLogs) {
            return { success: false, reason: `Need ${logs.inputQuantity} ${logs.name}` };
        }

        // Check if output can fit in inventory
        const outputItem = logs.drops[0]?.itemId;
        if (outputItem && !canFitItem(outputItem)) {
            return { success: false, reason: 'Inventory full' };
        }

        return { success: true };
    }

    startBurning(logsId, showToast = true) {
        // Find logs by id property, not by object key
        const logs = Object.values(LOGS).find(l => l.id === logsId);
        const canBurn = this.canBurn(logsId);

        if (!canBurn.success) {
            if (showToast) toastWarning(canBurn.reason);
            return false;
        }

        // Calculate burning time based on logs and effective speed
        const baseTime = logs.baseTime;
        const speedMultiplier = this.getEffectiveSpeedMultiplier();
        const burningTime = baseTime / speedMultiplier;

        const action = {
            type: 'pyre',
            name: `Burning ${logs.name}`,
            log: logs,
            logId: logsId.toUpperCase(),
            progress: 0,
            duration: burningTime,
            startTime: Date.now(),
            forceComplete: false,

            // Tick function called every game tick
            // speedMultiplier param allows admin panel to speed up actions
            tick: (speedMultiplier = 1) => {
                // Check if we still have materials - stop immediately if not
                const hasMaterials = gameState.getItemCount(logs.inputItem) >= logs.inputQuantity;
                if (!hasMaterials) {
                    gameState.clearAction();
                    return;
                }

                // Check for force complete (used by admin panel)
                if (action.forceComplete) {
                    action.forceComplete = false;
                    this.completeBurning(logs);
                    const canContinue = this.canBurn(logsId);
                    if (canContinue.success) {
                        this.startBurning(logsId, false);
                    } else {
                        gameState.clearAction();
                    }
                    return;
                }

                // Apply speed multiplier to elapsed time
                const elapsed = (Date.now() - action.startTime) * speedMultiplier;
                action.progress = Math.min(elapsed / action.duration, 1);

                if (action.progress >= 1) {
                    this.completeBurning(logs);
                    const canContinue = this.canBurn(logsId);
                    if (canContinue.success) {
                        this.startBurning(logsId, false);
                    } else {
                        gameState.clearAction();
                    }
                }
            }
        };

        gameState.setAction(action);

        // Check for instant burn (Inferno talent proc from previous action)
        if (this.nextBurnInstant) {
            this.nextBurnInstant = false;
            // Immediately complete this burn
            this.completeBurning(logs);
            // Check if we can continue
            const canContinue = this.canBurn(logsId);
            if (canContinue.success) {
                this.startBurning(logsId, false);
            } else {
                gameState.clearAction();
            }
        }

        return true;
    }

    completeBurning(logs) {
        // Check for log save proc (Efficient Burn talent)
        const logSaved = rollTalentProc(SKILLS.PYRE, 'logSave');

        if (!logSaved) {
            // Consume logs from inventory
            const removed = gameState.removeItem(logs.inputItem, logs.inputQuantity);
            if (!removed) {
                logger.warn('Failed to remove logs for burning');
                this.stopBurning();
                return;
            }
        } else {
            recordTalentProc(SKILLS.PYRE, 'Efficient Burn', 'Logs saved!', null);
        }

        // Pass logs.id for admin node XP override
        const xpResult = addXP(SKILLS.PYRE, logs.xp, logs.id);

        // Calculate drops with talent bonuses
        let drops = [...logs.drops];
        let cinderStormProc = false;
        let bonusAshesProc = false;

        // Apply loot bonus to ash quantity (Ash Mastery talent)
        const lootBonus = getTalentLootBonus(SKILLS.PYRE);
        if (lootBonus > 0) {
            drops = drops.map(drop => {
                if (drop.itemId === 'ashes') {
                    const bonusQty = Math.floor(drop.quantity * lootBonus);
                    return { ...drop, quantity: drop.quantity + bonusQty };
                }
                return drop;
            });
        }

        // Check for Cinder Storm proc (3x ashes)
        if (rollTalentProc(SKILLS.PYRE, 'cinderStorm')) {
            drops = drops.map(d => d.itemId === 'ashes' ? { ...d, quantity: d.quantity * 3 } : d);
            cinderStormProc = true;
        }
        // Check for bonus ashes proc (Smoke Reading talent)
        else if (rollTalentProc(SKILLS.PYRE, 'bonusAshes')) {
            const ashDrop = drops.find(d => d.itemId === 'ashes');
            if (ashDrop) {
                // Add 1 bonus ash on proc
                drops = drops.map(d => d.itemId === 'ashes' ? { ...d, quantity: d.quantity + 1 } : d);
                bonusAshesProc = true;
            }
        }

        // Award drops (handles corruption loot multiplier centrally)
        awardDrops(SKILLS.PYRE, drops);

        // Record proc notifications after drops awarded
        const ashDrop = drops.find(d => d.itemId === 'ashes');
        if (cinderStormProc && ashDrop) {
            recordTalentProc(SKILLS.PYRE, 'Cinder Storm', null, null, { itemId: 'ashes', quantity: ashDrop.quantity });
        } else if (bonusAshesProc && ashDrop) {
            recordTalentProc(SKILLS.PYRE, 'Smoke Reading', null, null, { itemId: 'ashes', quantity: ashDrop.quantity });
        }

        // Roll for corruption proc
        rollForCorruption(SKILLS.PYRE);

        // Increment stacking speed count (Blazing Rhythm talent)
        incrementStackCount(SKILLS.PYRE, logs.id);

        // Check for Pyre Veil lore triggers on level-up
        if (xpResult.leveledUp) {
            const loreTriggers = checkPyreLoreTriggers(xpResult.oldLevel, xpResult.newLevel);
            if (loreTriggers.length > 0) {
                showPyreVeilLoreQueue(loreTriggers);
            }
        }

        this.onBurningComplete(logs, xpResult, logSaved);

        // Check for instant burn proc (Inferno talent) - applies to next action
        if (rollTalentProc(SKILLS.PYRE, 'instantBurn')) {
            this.nextBurnInstant = true;
            recordTalentProc(SKILLS.PYRE, 'Inferno', 'Instant next burn!', null);
        }
    }

    stopBurning() {
        resetStackCount(SKILLS.PYRE);
        this.nextBurnInstant = false;
        gameState.clearAction();
    }

    /**
     * Purchase or equip a tool
     */
    upgradeTool(toolId) {
        const tool = Object.values(TOOLS).find(t => t.id === toolId);
        if (!tool) return { success: false, reason: 'Invalid tool' };

        const skill = gameState.getSkill(SKILLS.PYRE);
        if (skill.level < tool.level) {
            return { success: false, reason: `Requires level ${tool.level} Pyre` };
        }

        // Check if already owned
        if (gameState.hasPurchasedTool(SKILLS.PYRE, toolId)) {
            // Just equip it
            gameState.equipTool(SKILLS.PYRE, toolId);
            return { success: true, action: 'equipped' };
        }

        // Try to purchase
        const purchaseResult = gameState.purchaseTool(SKILLS.PYRE, toolId, tool.price);
        if (!purchaseResult.success) {
            return purchaseResult;
        }

        // Auto-equip after purchase
        gameState.equipTool(SKILLS.PYRE, toolId);
        return { success: true, action: 'purchased' };
    }

    getAvailableLogs() {
        const skill = gameState.getSkill(SKILLS.PYRE);
        return Object.values(LOGS).filter(logs => logs.level <= skill.level);
    }

    onBurningComplete(logs, xpResult, logSaved = false) {
        logger.game(`Burned ${logs.name}! +${logs.xp} XP`);
        if (xpResult.leveledUp) {
            logger.game(`Pyre level up! Now level ${xpResult.newLevel}`);
        }
    }

    // ==================== RITUAL METHODS ====================

    /**
     * Check if player can perform a ritual with given log and bone
     */
    canPerformRitual(logId, ritualId) {
        const logs = Object.values(LOGS).find(l => l.id === logId);
        if (!logs) return { success: false, reason: 'Logs not found' };

        const ritual = Object.values(RITUALS).find(r => r.id === ritualId);
        if (!ritual) return { success: false, reason: 'Ritual not found' };

        // Check if log tier is allowed for this ritual
        if (!canLogBeUsedInRitual(logs, ritual)) {
            return { success: false, reason: `${logs.name} cannot be used with ${ritual.name}` };
        }

        const skill = gameState.getSkill(SKILLS.PYRE);

        // Check log level requirement
        if (skill.level < logs.level) {
            return { success: false, reason: `Requires level ${logs.level} Pyre for ${logs.name}` };
        }

        // Check ritual level requirement
        if (skill.level < ritual.level) {
            return { success: false, reason: `Requires level ${ritual.level} Pyre for ${ritual.name}` };
        }

        // Check for required logs in inventory
        const hasLogs = gameState.getItemCount(logs.inputItem) >= logs.inputQuantity;
        if (!hasLogs) {
            return { success: false, reason: `Need ${logs.inputQuantity} ${logs.name}` };
        }

        // Check for required bone in inventory
        const hasBone = gameState.getItemCount(ritual.boneId) >= 1;
        if (!hasBone) {
            return { success: false, reason: `Need 1 ${ritual.boneName}` };
        }

        // Check if ashes can fit in inventory
        if (!canFitItem('ashes')) {
            return { success: false, reason: 'Inventory full' };
        }

        return { success: true };
    }

    /**
     * Start performing a ritual (log + bone burn)
     */
    startRitual(logId, ritualId, showToast = true) {
        const logs = Object.values(LOGS).find(l => l.id === logId);
        const ritual = Object.values(RITUALS).find(r => r.id === ritualId);
        const canPerform = this.canPerformRitual(logId, ritualId);

        if (!canPerform.success) {
            if (showToast) toastWarning(canPerform.reason);
            return false;
        }

        // Calculate ritual time using effective speed
        const baseTime = logs.baseTime;
        const speedMultiplier = this.getEffectiveSpeedMultiplier();
        const ritualTime = baseTime / speedMultiplier;

        // Calculate ritual XP (base log XP * ritual multiplier)
        const ritualXp = Math.floor(logs.xp * ritual.xpMultiplier);

        const action = {
            type: 'pyre_ritual',
            name: `${ritual.name}: ${logs.name}`,
            log: logs,
            logId: logId,
            ritual: ritual,
            ritualId: ritualId,
            ritualXp: ritualXp,
            progress: 0,
            duration: ritualTime,
            startTime: Date.now(),
            forceComplete: false,

            tick: (speedMultiplier = 1) => {
                // Check if we still have materials
                const hasLogs = gameState.getItemCount(logs.inputItem) >= logs.inputQuantity;
                const hasBone = gameState.getItemCount(ritual.boneId) >= 1;
                if (!hasLogs || !hasBone) {
                    gameState.clearAction();
                    return;
                }

                // Check for force complete
                if (action.forceComplete) {
                    action.forceComplete = false;
                    this.completeRitual(logs, ritual, ritualXp);
                    const canContinue = this.canPerformRitual(logId, ritualId);
                    if (canContinue.success) {
                        this.startRitual(logId, ritualId, false);
                    } else {
                        gameState.clearAction();
                    }
                    return;
                }

                // Apply speed multiplier to elapsed time
                const elapsed = (Date.now() - action.startTime) * speedMultiplier;
                action.progress = Math.min(elapsed / action.duration, 1);

                if (action.progress >= 1) {
                    this.completeRitual(logs, ritual, ritualXp);
                    const canContinue = this.canPerformRitual(logId, ritualId);
                    if (canContinue.success) {
                        this.startRitual(logId, ritualId, false);
                    } else {
                        gameState.clearAction();
                    }
                }
            }
        };

        gameState.setAction(action);
        return true;
    }

    /**
     * Complete a ritual action
     */
    completeRitual(logs, ritual, ritualXp) {
        // Check for log save proc (Efficient Burn talent)
        const logSaved = rollTalentProc(SKILLS.PYRE, 'logSave');

        // Check for bone save proc (Spiritual Offering talent)
        const boneSaved = rollTalentProc(SKILLS.PYRE, 'boneSave');

        if (!logSaved) {
            // Consume logs from inventory
            const removedLogs = gameState.removeItem(logs.inputItem, logs.inputQuantity);
            if (!removedLogs) {
                logger.warn('Failed to remove logs for ritual');
                this.stopBurning();
                return;
            }
        } else {
            recordTalentProc(SKILLS.PYRE, 'Efficient Burn', 'Logs saved!', null);
        }

        if (!boneSaved) {
            // Consume bone from inventory
            const removedBone = gameState.removeItem(ritual.boneId, 1);
            if (!removedBone) {
                logger.warn('Failed to remove bone for ritual');
                // Refund the logs if we consumed them
                if (!logSaved) {
                    gameState.addItem(logs.inputItem, logs.inputQuantity);
                }
                this.stopBurning();
                return;
            }
        } else {
            recordTalentProc(SKILLS.PYRE, 'Spiritual Offering', 'Bone saved!', null);
        }

        // Calculate effective XP with ritual talent bonus
        const ritualXpBonus = getTalentFlatBonus(SKILLS.PYRE, 'ritualXpBonus');
        const effectiveXp = Math.floor(ritualXp * (1 + ritualXpBonus));

        // Award XP with talent-enhanced ritual multiplier
        const xpResult = addXP(SKILLS.PYRE, effectiveXp, logs.id);

        // Calculate ashes with talent bonuses
        let ashesAmount = getRitualAshesOutput(logs.tier);

        // Apply loot bonus to ash quantity (Ash Mastery talent)
        const lootBonus = getTalentLootBonus(SKILLS.PYRE);
        if (lootBonus > 0) {
            ashesAmount += Math.floor(ashesAmount * lootBonus);
        }

        // Check for Cinder Storm proc (3x ashes)
        let cinderStormProc = false;
        let bonusAshesProc = false;
        if (rollTalentProc(SKILLS.PYRE, 'cinderStorm')) {
            ashesAmount *= 3;
            cinderStormProc = true;
        }
        // Check for bonus ashes proc (Smoke Reading talent)
        else if (rollTalentProc(SKILLS.PYRE, 'bonusAshes')) {
            ashesAmount += 1;
            bonusAshesProc = true;
        }

        const ritualDrops = [{ itemId: 'ashes', weight: 1, quantity: ashesAmount }];
        awardDrops(SKILLS.PYRE, ritualDrops);

        // Record proc notifications after drops awarded
        if (cinderStormProc) {
            recordTalentProc(SKILLS.PYRE, 'Cinder Storm', null, null, { itemId: 'ashes', quantity: ashesAmount });
        } else if (bonusAshesProc) {
            recordTalentProc(SKILLS.PYRE, 'Smoke Reading', null, null, { itemId: 'ashes', quantity: ashesAmount });
        }

        // Check for Veil's Blessing proc (potion reward based on Alchemy level)
        if (rollTalentProc(SKILLS.PYRE, 'veilBlessing')) {
            this.awardVeilBlessingPotion();
        }

        // Roll for corruption proc
        rollForCorruption(SKILLS.PYRE);

        // Increment stacking speed count (Blazing Rhythm talent)
        incrementStackCount(SKILLS.PYRE, `ritual_${logs.id}`);

        // Check for Pyre Veil lore triggers on level-up
        if (xpResult.leveledUp) {
            const loreTriggers = checkPyreLoreTriggers(xpResult.oldLevel, xpResult.newLevel);
            if (loreTriggers.length > 0) {
                showPyreVeilLoreQueue(loreTriggers);
            }
        }

        this.onRitualComplete(logs, ritual, effectiveXp, xpResult, logSaved, boneSaved);
    }

    /**
     * Called when a ritual completes
     */
    onRitualComplete(logs, ritual, ritualXp, xpResult, logSaved = false, boneSaved = false) {
        logger.game(`${ritual.name} complete! Burned ${logs.name} with ${ritual.boneName}! +${ritualXp} XP`);
        if (xpResult.leveledUp) {
            logger.game(`Pyre level up! Now level ${xpResult.newLevel}`);
        }
    }

    /**
     * Award a random potion based on Alchemy level (Veil's Blessing talent)
     * Only triggers during rituals
     */
    awardVeilBlessingPotion() {
        const alchemyLevel = gameState.getSkill(SKILLS.ALCHEMY)?.level || 1;

        // Get all potions the player can make based on Alchemy level
        const availablePotions = Object.values(POTION_RECIPES).filter(p => p.level <= alchemyLevel);

        if (availablePotions.length === 0) {
            // Fallback to basic healing potion if no alchemy level
            gameState.addItem('healing_potion_1', 1);
            recordTalentProc(SKILLS.PYRE, "Veil's Blessing", null, null, { itemId: 'healing_potion_1', quantity: 1 });
            return;
        }

        // Select a random available potion
        const randomPotion = availablePotions[Math.floor(Math.random() * availablePotions.length)];

        // Get the output item from the potion recipe
        const potionDrop = randomPotion.drops?.[0];
        if (potionDrop) {
            gameState.addItem(potionDrop.itemId, 1);
            recordTalentProc(SKILLS.PYRE, "Veil's Blessing", null, null, { itemId: potionDrop.itemId, quantity: 1 });
        }
    }

    /**
     * Get rituals available at current Pyre level
     */
    getUnlockedRituals() {
        const skill = gameState.getSkill(SKILLS.PYRE);
        return getAvailableRituals(skill.level);
    }

    /**
     * Get all rituals (for UI display with locked state)
     */
    getAllRituals() {
        return Object.values(RITUALS);
    }
}

export const pyreSkill = new PyreSkill();
export default pyreSkill;
